import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '../widgets/Button'
import type { ChangeCardChange, ChangeCardSet } from './ChangeCard'

/**
 * A set card's changes (SEN-460, "many small"): ONE card for one intent over many records — 261 assets
 * get Category Retail, 41 meters get forecasting off. Identical changes (same field, same new value) are
 * ONE group: "261 × Category: (empty) → Retail", its records listed on demand, a page at a time. Changes
 * that differ per record are rows, also paged. Records the server left out (`set.refused`) are said, with
 * the reason. Every layout is a stack that wraps — no table, so nothing scrolls sideways on a phone.
 */

export const RECORDS_PAGE = 24
const GROUPS_PAGE = 8

interface ChangeGroup {
  key: string
  field: string
  /** one change of the group (for its new value's read cell) */
  sample: ChangeCardChange
  records: Array<{ record: string; old: string | null | undefined; change: ChangeCardChange }>
  /** the distinct old values, most common first */
  olds: Array<{ text: string; count: number }>
}

const oldText = (c: ChangeCardChange) => (c.old === undefined || c.old === null ? '(empty)' : c.old)

/** Group identical changes: same field and same new value (by the raw value when the server sent it). */
export function groupChanges(changes: ChangeCardChange[]): ChangeGroup[] {
  const byKey = new Map<string, ChangeGroup>()
  for (const c of changes) {
    const newKey = c.new_value !== undefined ? JSON.stringify(c.new_value) : JSON.stringify(c.value ?? null)
    const key = `${c.field}\u0000${newKey}`
    let g = byKey.get(key)
    if (!g) { g = { key, field: c.field, sample: c, records: [], olds: [] }; byKey.set(key, g) }
    g.records.push({ record: c.record, old: c.old, change: c })
  }
  const groups = [...byKey.values()]
  for (const g of groups) {
    const counts = new Map<string, number>()
    for (const r of g.records) counts.set(oldText(r.change), (counts.get(oldText(r.change)) ?? 0) + 1)
    g.olds = [...counts.entries()].map(([text, count]) => ({ text, count })).sort((a, b) => b.count - a.count)
  }
  // the big groups first; a stable order otherwise (the server's)
  return groups.map((g, i) => ({ g, i })).sort((a, b) => b.g.records.length - a.g.records.length || a.i - b.i).map((x) => x.g)
}

/** "Category → Retail · 261 records" when the server sent no summary line. */
export function setHeadline(groups: ChangeGroup[], records: number): string {
  const n = `${records} ${records === 1 ? 'record' : 'records'}`
  if (groups.length === 1) return `${groups[0]!.field} → ${groups[0]!.sample.value ?? '(empty)'} · ${n}`
  const fields = new Set(groups.map((g) => g.field))
  return fields.size === 1 ? `${[...fields][0]} · ${n}` : `${fields.size} fields · ${n}`
}

function Pager({ page, size, total, onPage, testId }: { page: number; size: number; total: number; onPage: (p: number) => void; testId: string }) {
  if (total <= size) return null
  const last = Math.ceil(total / size) - 1
  return <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground" data-testid={`${testId}-pager`}>
    <span className="tabular-nums">{page * size + 1}–{Math.min(total, (page + 1) * size)} of {total}</span>
    <span className="flex-1" />
    <Button variant="quiet" data-testid={`${testId}-prev`} disabled={page === 0} onClick={() => onPage(page - 1)}>Previous</Button>
    <Button variant="quiet" data-testid={`${testId}-next`} disabled={page >= last} onClick={() => onPage(page + 1)}>Next</Button>
  </div>
}

type ValueRender = (change: ChangeCardChange, which: 'old' | 'new') => ReactNode

/** One group of identical changes: the change once, with how many records; its records on demand. */
function Group({ group, value }: { group: ChangeGroup; value: ValueRender }) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(0)
  const n = group.records.length
  const sameOld = group.olds.length === 1
  const shown = group.records.slice(page * RECORDS_PAGE, (page + 1) * RECORDS_PAGE)
  return <div data-testid="change-group" className="min-w-0 rounded-lg border border-border">
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
      <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums" data-testid="change-group-count">{n} ×</span>
      <span className="font-medium">{group.field}</span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-1.5" data-testid="change-group-change">
        <span className="text-muted-foreground">
          {sameOld ? value(group.records[0]!.change, 'old') : `${group.olds.length} different values`}
        </span>
        <span aria-label="becomes" className="text-muted-foreground">→</span>
        <span className="font-medium">{value(group.sample, 'new')}</span>
      </span>
    </div>
    {!sameOld && <p className="px-3 pb-1 text-xs text-muted-foreground" data-testid="change-group-olds">
      Now: {group.olds.slice(0, 4).map((o) => `${o.text} (${o.count})`).join(', ')}{group.olds.length > 4 ? ', …' : ''}
    </p>}
    <button type="button" data-testid="change-group-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}
      className="flex w-full items-center gap-1 border-t border-border px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground">
      {open ? <ChevronDown aria-hidden className="size-4" /> : <ChevronRight aria-hidden className="size-4" />}
      {open ? 'Hide the records' : n === 1 ? `Show the record` : `Show all ${n} records`}
    </button>
    {open && <div className="border-t border-border px-3 py-2" data-testid="change-group-records">
      <ul className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {shown.map((r, i) => <li key={`${page}-${i}`} data-testid="change-group-record" className="min-w-0 break-words">
          {r.record}{!sameOld && <span className="text-xs text-muted-foreground"> · now {oldText(r.change)}</span>}
        </li>)}
      </ul>
      <Pager page={page} size={RECORDS_PAGE} total={n} onPage={setPage} testId="change-group" />
    </div>}
  </div>
}

/** Changes that differ per record: one stacked row each (record, then field old → new), paged. */
function Rows({ rows, value }: { rows: ChangeGroup[]; value: ValueRender }) {
  const [page, setPage] = useState(0)
  const shown = rows.slice(page * GROUPS_PAGE, (page + 1) * GROUPS_PAGE)
  return <div className="min-w-0 rounded-lg border border-border" data-testid="change-set-rows">
    <ul className="divide-y divide-border">
      {shown.map((g) => {
        const c = g.records[0]!.change
        return <li key={g.key} data-testid="change-set-row" className="grid min-w-0 gap-0.5 px-3 py-2">
          <span className="break-words text-xs text-muted-foreground">{c.record}</span>
          <span className="flex min-w-0 flex-wrap items-center gap-x-1.5">
            <span className="font-medium">{c.field}</span>
            <span className="text-muted-foreground">{value(c, 'old')}</span>
            <span aria-label="becomes" className="text-muted-foreground">→</span>
            <span>{value(c, 'new')}</span>
          </span>
        </li>
      })}
    </ul>
    {rows.length > GROUPS_PAGE && <div className="border-t border-border px-3 py-1.5">
      <Pager page={page} size={GROUPS_PAGE} total={rows.length} onPage={setPage} testId="change-set-rows" />
    </div>}
  </div>
}

function Refused({ refused }: { refused: ChangeCardSet['refused'] }) {
  const [open, setOpen] = useState(false)
  if (!refused.length) return null
  return <div data-testid="change-set-refused" className="text-xs">
    <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="change-set-refused-toggle"
      className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
      {open ? <ChevronDown aria-hidden className="size-4" /> : <ChevronRight aria-hidden className="size-4" />}
      {refused.length === 1 ? '1 record is left out' : `${refused.length} records are left out`}
    </button>
    {open && <ul className="mt-1 grid gap-1 pl-5">
      {refused.map((r, i) => <li key={i} className="break-words"><span className="font-medium text-foreground">{r.record}</span>
        <span className="text-muted-foreground"> · {r.reason}</span></li>)}
    </ul>}
  </div>
}

export function ChangeSetView({ changes, set, value }: { changes: ChangeCardChange[]; set?: ChangeCardSet | null; value: ValueRender }) {
  const groups = useMemo(() => groupChanges(changes), [changes])
  const [allGroups, setAllGroups] = useState(false)
  const many = groups.filter((g) => g.records.length > 1)
  const single = groups.filter((g) => g.records.length === 1)
  const shownGroups = allGroups ? many : many.slice(0, GROUPS_PAGE)
  return <div className="grid min-w-0 gap-2" data-testid="change-set">
    {shownGroups.map((g) => <Group key={g.key} group={g} value={value} />)}
    {many.length > shownGroups.length && <Button variant="quiet" data-testid="change-set-more-groups" onClick={() => setAllGroups(true)}>
      Show {many.length - shownGroups.length} more changes</Button>}
    {single.length > 0 && <Rows rows={single} value={value} />}
    {set && <Refused refused={set.refused ?? []} />}
  </div>
}
